import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
    addManualBadgeage,
    deleteBadgeage,
    getDailyFollowUpReport,
} from "../../../../_api/TimeSlot";
import { Table } from "../../../../_helpers/Table";
import { Button } from "../../../../_helpers/components/Button";
import toast from "react-hot-toast";

const toYmd = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const formatDayLong = (ymd) => {
    if (!ymd) return "";
    const [y, m, d] = ymd.split("-").map((p) => Number(p));
    const date = new Date(y, (m || 1) - 1, d || 1);
    if (Number.isNaN(date.getTime())) return ymd;
    return date.toLocaleDateString(undefined, {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
    });
};

const formatDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatHourMinute = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
    });
};

const isHighlightRow = (expected, actual) =>
    actual !== expected && (actual === 0 || actual % 2 === 1);

const FILTER_MODES = {
    ALL: "all",
    RELEVANT: "relevant",
    ANOMALIES: "anomalies",
};

const FILTER_LABELS = {
    [FILTER_MODES.ALL]: "Tout afficher",
    [FILTER_MODES.RELEVANT]: "Avec pointages attendus ou pointage réalisé",
    [FILTER_MODES.ANOMALIES]: "Uniquement les anomalies",
};

const EMPTY_MESSAGES = {
    [FILTER_MODES.ALL]: "Aucun planning ni pointage sur la période sélectionnée.",
    [FILTER_MODES.RELEVANT]: "Aucun pointage attendu ni pointage réalisé sur cette période.",
    [FILTER_MODES.ANOMALIES]: "Aucune anomalie sur la période sélectionnée.",
};

const matchesFilter = (mode, expected, actual) => {
    if (mode === FILTER_MODES.RELEVANT) {
        return expected > 0 || (expected === 0 && actual > 0);
    }
    if (mode === FILTER_MODES.ANOMALIES) {
        return (expected > 0 && actual === 0) || actual % 2 === 1;
    }
    return true;
};

const DailyFollowUp = () => {
    const [user] = useOutletContext();
    const today = useMemo(() => toYmd(new Date(new Date().setDate(new Date().getDate() - 1))), []);
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filterMode, setFilterMode] = useState(FILTER_MODES.ALL);

    const [manualForm, setManualForm] = useState({
        open: false,
        userId: null,
        userLabel: "",
        date: today,
        time: "",
        submitting: false,
        error: null,
    });

    const [deleteConfirm, setDeleteConfirm] = useState({
        open: false,
        badgeageId: null,
        userId: null,
        date: null,
        userLabel: "",
        badgedAtLabel: "",
        submitting: false,
        error: null,
    });

    const hasMenuAccess =
        user?.policy?.rights?.PLANNING?.DAILY_FOLLOW_UP?.access === true;

    const planningMain =
        user?.policy?.rights?.PLANNING?.PLANNING_MENU?.sub_menu?.MAIN ?? {};
    const planningFeatures = planningMain?.features ?? {};
    const canSeeMyChecking = planningFeatures?.see_my_checking === true;
    const canSeeOthersChecking = planningFeatures?.see_others_checking === true;
    const canAddPunch = canSeeMyChecking || canSeeOthersChecking;

    const loadReport = useCallback(async () => {
        if (!hasMenuAccess) return;
        if (!startDate || !endDate || endDate < startDate) {
            setError("La date de fin doit être supérieure ou égale à la date de début.");
            setReport(null);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const resp = await getDailyFollowUpReport(startDate, endDate);
            setReport(resp.data?.data ?? null);
        } catch (e) {
            const msg =
                e.response?.data?.message ||
                "Impossible de charger le rapport de pointages.";
            setError(msg);
            setReport(null);
            if (e.response?.status === 403) {
                toast.error(msg);
            }
        } finally {
            setLoading(false);
        }
    }, [hasMenuAccess, startDate, endDate]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    const visibleDays = useMemo(() => {
        if (!report?.days) return [];
        return report.days
            .map((day) => ({
                ...day,
                users: (day.users ?? []).filter((u) => {
                    const hasContent =
                        (u.provisional_slots?.length ?? 0) > 0 ||
                        (u.badgeages?.length ?? 0) > 0;
                    if (!hasContent) return false;
                    return matchesFilter(
                        filterMode,
                        u.expected_pointages ?? 0,
                        u.badgeages?.length ?? 0
                    );
                }),
            }))
            .filter((day) => day.users.length > 0);
    }, [report, filterMode]);

    const buildTableRows = (day) =>
        day.users.map((row) => {
            const expected = row.expected_pointages ?? 0;
            const actual = row.badgeages?.length ?? 0;
            const label = `${row.last_name ?? ""} ${row.first_name ?? ""}`.trim();
            const highlight = isHighlightRow(expected, actual);
            return {
                row: [label || "—", String(expected), String(actual)],
                data: {
                    id: `${day.date}-${row.id}`,
                    userId: row.id,
                    userLabel: label || "—",
                    date: day.date,
                    expected_pointages: expected,
                    provisional_slots: row.provisional_slots ?? [],
                    badgeages: row.badgeages ?? [],
                    rowClassName: highlight ? "bg-red-50 hover:bg-red-100" : undefined,
                },
            };
        });

    const openManualForm = (data) => {
        setManualForm({
            open: true,
            userId: data.userId,
            userLabel: data.userLabel,
            date: data.date,
            time: "",
            submitting: false,
            error: null,
        });
    };

    const closeManualForm = () => {
        setManualForm((prev) => ({ ...prev, open: false, submitting: false, error: null }));
    };

    const submitManualPunch = async () => {
        if (!manualForm.time || !manualForm.date || !manualForm.userId) {
            setManualForm((prev) => ({ ...prev, error: "Heure requise." }));
            return;
        }
        const [hh, mm] = manualForm.time.split(":");
        if (hh === undefined || mm === undefined) {
            setManualForm((prev) => ({ ...prev, error: "Format heure invalide." }));
            return;
        }
        const [y, m, d] = manualForm.date.split("-").map((p) => Number(p));
        const local = new Date(y, (m || 1) - 1, d || 1, Number(hh), Number(mm), 0, 0);
        if (Number.isNaN(local.getTime())) {
            setManualForm((prev) => ({ ...prev, error: "Date/heure invalide." }));
            return;
        }
        if (local.getTime() > Date.now()) {
            setManualForm((prev) => ({ ...prev, error: "Le pointage ne peut pas être dans le futur." }));
            return;
        }

        setManualForm((prev) => ({ ...prev, submitting: true, error: null }));
        try {
            const resp = await addManualBadgeage({
                user_id: manualForm.userId,
                badged_at: local.toISOString(),
            });
            const newBadgeage = resp.data?.data;
            if (newBadgeage) {
                setReport((prev) => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        days: prev.days.map((day) => {
                            if (day.date !== manualForm.date) return day;
                            return {
                                ...day,
                                users: day.users.map((u) => {
                                    if (u.id !== manualForm.userId) return u;
                                    const next = [...(u.badgeages ?? []), newBadgeage];
                                    next.sort((a, b) => String(a.badged_at).localeCompare(String(b.badged_at)));
                                    return { ...u, badgeages: next };
                                }),
                            };
                        }),
                    };
                });
            }
            toast.success("Pointage ajouté.");
            closeManualForm();
        } catch (e) {
            const msg = e.response?.data?.message || "Échec de l'ajout du pointage.";
            setManualForm((prev) => ({ ...prev, submitting: false, error: msg }));
        }
    };

    const openDeleteConfirm = ({ badgeageId, userId, date, userLabel, badgedAt }) => {
        setDeleteConfirm({
            open: true,
            badgeageId,
            userId,
            date,
            userLabel,
            badgedAtLabel: formatDateTime(badgedAt),
            submitting: false,
            error: null,
        });
    };

    const closeDeleteConfirm = () => {
        setDeleteConfirm((prev) => ({ ...prev, open: false, submitting: false, error: null }));
    };

    const submitDelete = async () => {
        if (!deleteConfirm.badgeageId) {
            return;
        }
        setDeleteConfirm((prev) => ({ ...prev, submitting: true, error: null }));
        try {
            await deleteBadgeage(deleteConfirm.badgeageId);
            setReport((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    days: prev.days.map((day) => {
                        if (day.date !== deleteConfirm.date) return day;
                        return {
                            ...day,
                            users: day.users.map((u) => {
                                if (u.id !== deleteConfirm.userId) return u;
                                const next = (u.badgeages ?? []).filter(
                                    (b) => b.id !== deleteConfirm.badgeageId
                                );
                                return {
                                    ...u,
                                    badgeages: next,
                                    actual_pointages: next.length,
                                };
                            }),
                        };
                    }),
                };
            });
            toast.success("Pointage supprimé.");
            closeDeleteConfirm();
        } catch (e) {
            const msg = e.response?.data?.message || "Échec de la suppression du pointage.";
            setDeleteConfirm((prev) => ({ ...prev, submitting: false, error: msg }));
        }
    };

    const renderSubTable = (data) => (
        <div className="space-y-4 text-left">
            <div className="grid gap-6 sm:grid-cols-2">
                <div>
                    <h4 className="font-semibold text-gray-800 mb-2 text-sm">
                        Prévisionnel
                    </h4>
                    {data.provisional_slots?.length ? (
                        <ul className="space-y-1.5 text-sm text-gray-800 list-disc list-inside">
                            {data.provisional_slots.map((s) => (
                                <li key={s.id}>
                                    {formatHourMinute(s.start_date)} – {formatHourMinute(s.end_date)}
                                    {s.hour_type?.name ? ` (${s.hour_type.name})` : ""}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-gray-500">Aucun créneau prévisionnel.</p>
                    )}
                </div>
                <div>
                    <h4 className="font-semibold text-gray-800 mb-2 text-sm">
                        Pointage(s) réel(s)
                    </h4>
                    {data.badgeages?.length ? (
                        <ul className="space-y-1.5 text-sm text-gray-800">
                            {data.badgeages.map((b) => (
                                <li key={b.id} className="flex items-start gap-2">
                                    <span className="flex-1">
                                        {formatHourMinute(b.badged_at)}
                                        {b.badgeuse?.name
                                            ?
                                            <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs">
                                                {b.badgeuse.name} {b.badgeuse.remarks ? `(${b.badgeuse.remarks})` : ""}
                                            </span>
                                            :
                                            ""}
                                        {b.is_manual ? (
                                            <>
                                                <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs">
                                                    manuel
                                                </span>
                                                {b.comment ? (
                                                    <span className="ml-2 italic text-gray-500">
                                                        {b.comment}
                                                    </span>
                                                ) : null}
                                            </>
                                        ) : null}
                                    </span>
                                    {
                                        canAddPunch ? (
                                            <Button
                                                type="custom"
                                                title="Supprimer ce pointage"
                                                customCN="inline-flex items-center justify-center rounded-md border border-transparent p-1 text-red-700 hover:bg-red-50 hover:text-red-800 focus:outline-hidden focus:shadow-outline cursor-pointer"
                                                action={(ev) => {
                                                    if (ev?.stopPropagation) ev.stopPropagation();
                                                    openDeleteConfirm({
                                                        badgeageId: b.id,
                                                        userId: data.userId,
                                                        date: data.date,
                                                        userLabel: data.userLabel,
                                                        badgedAt: b.badged_at,
                                                    });
                                                }}
                                                name={
                                                    <>
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            className="h-4 w-4"
                                                            style={{ transform: "none" }}
                                                            aria-hidden="true"
                                                        >
                                                            <path d="M3 6h18" />
                                                            <path d="M8 6V4h8v2" />
                                                            <path d="M19 6l-1 14H6L5 6" />
                                                            <path d="M10 11v6" />
                                                            <path d="M14 11v6" />
                                                        </svg>
                                                        <span className="sr-only">Supprimer</span>
                                                    </>
                                                }
                                            />
                                        ) : null}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-gray-500">Aucun pointage enregistré.</p>
                    )}
                </div>
            </div>

            {
                canAddPunch ? (
                    <div className="pt-2 border-t border-gray-200">
                        <Button
                            type="empty"
                            name="Ajouter un pointage"
                            action={(ev) => {
                                if (ev?.stopPropagation) ev.stopPropagation();
                                openManualForm(data);
                            }}
                        />
                    </div>
                ) : null
            }
        </div >
    );

    if (!hasMenuAccess) {
        return (
            <div className="min-h-full bg-gray-50 p-6">
                <p className="text-gray-700">
                    Vous n&apos;avez pas accès au suivi quotidien.
                </p>
            </div>
        );
    }

    const tableHeaders = ["Collaborateur", "Pointages attendus", "Pointages réels"];

    return (
        <div className="min-h-full bg-gray-50">
            <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
                    <h1 className="text-3xl font-bold text-gray-900">
                        Suivi quotidien
                    </h1>
                    <p className="mt-2 text-sm text-gray-600">
                        Pointages attendus et pointages réels.
                        Cliquez sur une ligne pour afficher le détail.
                    </p>
                    {(!canSeeMyChecking && !canSeeOthersChecking) ? (
                        <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                            Vos droits ne permettent pas la consultation des pointages :
                            le rapport peut être refusé.
                        </p>
                    ) : null}
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6">
                <div className="flex flex-wrap items-end gap-4 mb-6">
                    <label className="block">
                        <span className="text-sm font-medium text-gray-700">Du</span>
                        <input
                            type="date"
                            className="mt-1 block rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm"
                            value={startDate}
                            onChange={(ev) => {
                                const v = ev.target.value;
                                setStartDate(v);
                                if (endDate && v && endDate < v) setEndDate(v);
                            }}
                        />
                    </label>
                    <label className="block">
                        <span className="text-sm font-medium text-gray-700">Au</span>
                        <input
                            type="date"
                            min={startDate || undefined}
                            className="mt-1 block rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm"
                            value={endDate}
                            onChange={(ev) => setEndDate(ev.target.value)}
                        />
                    </label>
                    <label className="block">
                        <span className="text-sm font-medium text-gray-700">Filtre</span>
                        <select
                            className="mt-1 block rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm bg-white"
                            value={filterMode}
                            onChange={(ev) => setFilterMode(ev.target.value)}
                        >
                            {Object.values(FILTER_MODES).map((mode) => (
                                <option key={mode} value={mode}>
                                    {FILTER_LABELS[mode]}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>

                {loading ? (
                    <p className="text-gray-600 text-sm">Chargement…</p>
                ) : null}
                {error && !loading ? (
                    <p className="text-red-600 text-sm mb-4">{error}</p>
                ) : null}

                {report && !loading ? (
                    <div className="mb-4 text-xs text-gray-500">
                        Fuseau : {report.timezone}
                    </div>
                ) : null}

                {report && !loading && visibleDays.length === 0 ? (
                    <p className="text-gray-600 text-sm">
                        {EMPTY_MESSAGES[filterMode] ?? EMPTY_MESSAGES[FILTER_MODES.ALL]}
                    </p>
                ) : null}

                {report && !loading && visibleDays.map((day) => (
                    <section key={day.date} className="mb-8">
                        <h2 className="text-lg font-semibold text-gray-800 mb-3 capitalize">
                            {formatDayLong(day.date)}
                        </h2>
                        <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                            <Table
                                hideTitle
                                name="ligne de rapport"
                                headers={tableHeaders}
                                rows={buildTableRows(day)}
                                renderSubTable={renderSubTable}
                            />
                        </div>
                    </section>
                ))}
            </div>

            {manualForm.open ? (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                    onMouseDown={(ev) => {
                        if (ev.target === ev.currentTarget) closeManualForm();
                    }}
                >
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Ajouter un pointage
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            <span className="font-medium">{manualForm.userLabel}</span> —{" "}
                            {formatDayLong(manualForm.date)}
                        </p>

                        <label className="block mb-4">
                            <span className="text-sm font-medium text-gray-700">
                                Heure du pointage
                            </span>
                            <input
                                type="time"
                                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm"
                                value={manualForm.time}
                                onChange={(ev) =>
                                    setManualForm((prev) => ({
                                        ...prev,
                                        time: ev.target.value,
                                        error: null,
                                    }))
                                }
                                step="60"
                            />
                            <span className="block mt-1 text-xs text-gray-500">
                                La date est fixée à celle de la ligne sélectionnée. L&apos;heure
                                ne peut pas être dans le futur.
                            </span>
                        </label>

                        {manualForm.error ? (
                            <p className="text-sm text-red-600 mb-3">{manualForm.error}</p>
                        ) : null}

                        <div className="flex justify-end gap-2">
                            <Button
                                type="empty"
                                name="Annuler"
                                action={closeManualForm}
                            />
                            <Button
                                type="primary"
                                name="Enregistrer"
                                action={submitManualPunch}
                                loading={manualForm.submitting}
                            />
                        </div>
                    </div>
                </div>
            ) : null}

            {deleteConfirm.open ? (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                    onMouseDown={(ev) => {
                        if (ev.target === ev.currentTarget) closeDeleteConfirm();
                    }}
                >
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Supprimer le pointage
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">
                            <span className="font-medium">{deleteConfirm.userLabel}</span> —{" "}
                            {deleteConfirm.badgedAtLabel}
                        </p>
                        <p className="text-sm text-gray-700 mb-4">
                            Confirmez-vous la suppression de ce pointage ? Cette action est
                            journalisée (date, heure et auteur de la suppression).
                        </p>

                        {deleteConfirm.error ? (
                            <p className="text-sm text-red-600 mb-3">{deleteConfirm.error}</p>
                        ) : null}

                        <div className="flex justify-end gap-2">
                            <Button
                                type="empty"
                                name="Annuler"
                                action={closeDeleteConfirm}
                            />
                            <Button
                                type="primary"
                                name="Supprimer"
                                action={submitDelete}
                                loading={deleteConfirm.submitting}
                            />
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default DailyFollowUp;
